import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogProps,
  useMediaQuery,
  Theme,
  Slide,
  Box,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { forwardRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Dependent, DependentType } from '@/types'
import { DependantsForm } from './dependents-form'
import {
  useDeleteDependent,
  useCreateDependent,
  useUpdateDependent,
} from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { useParams } from 'react-router-dom'
import { formatDate, parseISOtoDate, toast } from '@/helpers'
import { LoadingButton } from '@mui/lab'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { DependentFormData, dependentFormSchema } from './dependent-form.schema'

type AddDependantDialogProps = DialogProps & {
  onClose: () => void
  dependent?: Dependent | null
}

export function AddDependantDialog({
  dependent,
  ...props
}: AddDependantDialogProps) {
  const isUpdate = Boolean(dependent)

  const queryClient = useQueryClient()
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))
  const { company } = useCompany()
  const { partner_key } = useParams()

  const dependentForm = useForm<DependentFormData>({
    resolver: zodResolver(dependentFormSchema),
    values: {
      name: dependent?.name ?? '',
      tax_id: dependent?.tax_id ?? '',
      birthdate: dependent?.birthdate
        ? parseISOtoDate(dependent.birthdate)
        : null,
      type: dependent?.type ?? '',
      irrf_deduction: dependent?.irrf_deduction ?? '',
    },
  })

  const { mutate: createDependent, isPending: isCreatingDependent } =
    useCreateDependent({
      onSuccess: () => {
        toast.success('Dependente adicionado com sucesso')
        queryClient.invalidateQueries({
          queryKey: [
            'dependents',
            { companyKey: company.key!, partnerKey: partner_key! },
          ],
        })
        onClose()
      },
    })
  const { mutate: updateDependent, isPending: isUpdatingDependent } =
    useUpdateDependent({
      onSuccess: () => {
        toast.success('Dependente atualizado com sucesso')
        queryClient.invalidateQueries({
          queryKey: [
            'dependents',
            { companyKey: company.key!, partnerKey: partner_key! },
          ],
        })
        onClose()
      },
    })

  const { mutate: deleteDependent, isPending: isDeletingDependent } =
    useDeleteDependent({
      onSuccess: () => {
        toast.success('Dependente excluído com sucesso')
        queryClient.invalidateQueries({
          queryKey: [
            'dependents',
            { companyKey: company.key!, partnerKey: partner_key! },
          ],
        })
        onClose()
      },
    })

  const handleSubmitDependent = (data: DependentFormData) => {
    const payload = {
      companyKey: company.key!,
      partnerKey: partner_key!,
      dependentData: {
        ...data,
        type: data.type as DependentType,
        irrf_deduction: data.irrf_deduction as boolean,
        birthdate: data.birthdate
          ? formatDate(data.birthdate, 'yyyy-MM-dd')
          : undefined,
      },
    }

    if (isUpdate) {
      updateDependent(payload)
    } else {
      createDependent(payload)
    }
  }

  const handleDeleteDependent = () => {
    deleteDependent({
      companyKey: company.key!,
      dependentKey: dependent!.key,
    })
  }

  const onClose = () => {
    dependentForm.reset({
      name: '',
      tax_id: '',
      birthdate: null,
      type: '',
      irrf_deduction: '',
    })
    props.onClose()
  }

  return (
    <Dialog
      {...props}
      fullScreen={isMobile}
      TransitionComponent={isMobile ? Transition : undefined}
      component="form"
      onSubmit={dependentForm.handleSubmit(handleSubmitDependent)}
    >
      <DialogTitle>
        {isUpdate ? 'Editar dependente' : 'Adicionar dependente'}
      </DialogTitle>

      <DialogContent>
        <FormProvider {...dependentForm}>
          <DependantsForm />
        </FormProvider>

        {isUpdate && (
          <Box textAlign="center" mt={2}>
            <LoadingButton
              variant="text"
              color="error"
              onClick={handleDeleteDependent}
              loading={isDeletingDependent}
            >
              Excluir dependente
            </LoadingButton>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancelar
        </Button>
        <LoadingButton
          type="submit"
          loading={isCreatingDependent || isUpdatingDependent}
        >
          {isUpdate ? 'Salvar' : 'Adicionar'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

const Transition = forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
)
